/**
 * Lớp phủ vẽ từ điểm ảnh: báo cháy sáng / mất đen và "khác nhau ở đâu" (spec 2026-10-28 §4.3).
 * Mỗi lớp là một ảnh `data:` cùng tỉ lệ ảnh chính, đặt TRONG lớp phóng nên co giãn theo ảnh.
 */

import { useEffect, useMemo, useState } from "react";
import { clippingMask, diffMask, sameAspect } from "./pixels";
import { PIXELS_BLOCKED_REASON, readPixels, type PixelState } from "./usePixelReader";

export const DIFF_SHAPE_REASON = "Hai bản khác khung — không so từng điểm được";

function maskToUrl(mask: Uint8ClampedArray, w: number, h: number): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.putImageData(new ImageData(new Uint8ClampedArray(mask), w, h), 0, 0);
  return canvas.toDataURL("image/png");
}

export function useClippingOverlay(pixels: PixelState, enabled: boolean): string | null {
  return useMemo(() => {
    if (!enabled || pixels.status !== "ready") return null;
    const { data, w, h } = pixels.pixels;
    return maskToUrl(clippingMask(data), w, h);
  }, [enabled, pixels]);
}

export type DiffState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; url: string; percent: number }
  | { status: "blocked"; reason: string };

function loadCors(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("load"));
    img.src = url;
  });
}

/** So bản A với bản B trên cỡ của A (≤ 1600px). Hai bản khác tỉ lệ khung ⇒ khoá, không tô sai. */
export function useDiffOverlay(urlA: string | undefined, urlB: string | undefined, enabled: boolean): DiffState {
  const key = enabled && urlA && urlB ? `${urlA}\n${urlB}` : "";
  const [state, setState] = useState<{ key: string; value: DiffState }>({ key: "", value: { status: "idle" } });

  useEffect(() => {
    if (!key || !urlA || !urlB) return;
    let alive = true;
    const done = (value: DiffState) => {
      if (alive) setState({ key, value });
    };
    Promise.all([loadCors(urlA), loadCors(urlB)])
      .then(([a, b]) => {
        if (!sameAspect({ w: a.naturalWidth, h: a.naturalHeight }, { w: b.naturalWidth, h: b.naturalHeight })) {
          done({ status: "blocked", reason: DIFF_SHAPE_REASON });
          return;
        }
        const pa = readPixels(a);
        const canvas = document.createElement("canvas");
        canvas.width = pa.w;
        canvas.height = pa.h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("no-2d");
        ctx.drawImage(b, 0, 0, pa.w, pa.h);
        const pb = ctx.getImageData(0, 0, pa.w, pa.h).data;
        const { mask, changed } = diffMask(pa.data, pb);
        const url = maskToUrl(mask, pa.w, pa.h);
        if (!url) throw new Error("no-2d");
        done({ status: "ready", url, percent: (changed / (pa.w * pa.h)) * 100 });
      })
      .catch(() => done({ status: "blocked", reason: PIXELS_BLOCKED_REASON }));
    return () => {
      alive = false;
    };
  }, [key, urlA, urlB]);

  if (!key) return { status: "idle" };
  return state.key === key ? state.value : { status: "loading" };
}
